import { deleteField, doc, getDoc, serverTimestamp, setDoc } from 'firebase/firestore';
import { db } from './firebase';

const INVITE_LOOKUP = 'inviteLookup';
const USER_CHAT_KEYS = 'userChatKeys';

/** Публичные поля в inviteLookup — видны любому signed-in (для списка контактов / аватара). */
export type InviteLookupPublicProfile = {
  displayName: string | null;
  photoURL: string | null;
};

export function parseInviteLookupPublicProfile(
  data: Record<string, unknown> | undefined
): InviteLookupPublicProfile {
  if (!data) return { displayName: null, photoURL: null };
  const dn = data.displayName;
  const ph = data.photoURL;
  return {
    displayName: typeof dn === 'string' && dn.trim() ? dn.trim() : null,
    photoURL: typeof ph === 'string' && ph.trim() ? ph.trim() : null,
  };
}

/** Слова из displayName (нижний регистр) для поиска по отдельному слову в People. */
export function buildNameSearchTokens(displayName: string): string[] {
  const parts = displayName
    .toLowerCase()
    .split(/[\s\-_.]+/)
    .map((t) => t.trim())
    .filter((t) => t.length >= 2);
  return Array.from(new Set(parts)).slice(0, 32);
}

/** Владелец токена обновляет отображаемое имя и аватар для гостей и контактов. */
export async function mergeInviteLookupPublicProfile(
  uid: string,
  inviteToken: string,
  profile: {
    displayName?: string | null;
    photoURL?: string | null;
    email?: string | null;
    discoverableInPeopleSearch?: boolean;
  }
): Promise<void> {
  if (!uid || !inviteToken) return;
  const patch: Record<string, unknown> = {
    uid,
    profileUpdatedAt: serverTimestamp(),
  };
  if (typeof profile.discoverableInPeopleSearch === 'boolean') {
    patch.discoverableInPeopleSearch = profile.discoverableInPeopleSearch;
  }
  if (profile.email !== undefined) {
    const em = profile.email != null ? String(profile.email).trim().toLowerCase() : '';
    if (em) patch.emailLower = em;
    else patch.emailLower = deleteField();
  }
  if (profile.displayName !== undefined) {
    const dn = profile.displayName != null ? String(profile.displayName).trim() : '';
    if (dn) {
      patch.displayName = dn;
      patch.displayNameLower = dn.toLowerCase();
      patch.nameTokens = buildNameSearchTokens(dn);
    } else {
      patch.displayName = deleteField();
      patch.displayNameLower = deleteField();
      patch.nameTokens = [];
    }
  }
  const ph = profile.photoURL != null ? String(profile.photoURL).trim() : '';
  if (ph) patch.photoURL = ph;
  await setDoc(doc(db, INVITE_LOOKUP, inviteToken), patch, { merge: true });
}

/**
 * Первая запись выигрывает: токен привязан к uid владельца (нельзя перезаписать чужой токен).
 */
export async function ensureInviteLookup(uid: string, inviteToken: string): Promise<void> {
  if (!uid || !inviteToken) return;
  const ref = doc(db, INVITE_LOOKUP, inviteToken);
  const snap = await getDoc(ref);
  if (!snap.exists()) {
    try {
      await setDoc(ref, { uid });
    } catch (e) {
      const again = await getDoc(ref);
      const u = again.exists() ? (again.data() as { uid?: string }).uid : undefined;
      if (u !== uid) {
        throw e instanceof Error ? e : new Error('invite_lookup_create_failed');
      }
    }
    return;
  }
  const existing = snap.data() as { uid?: string };
  const existingUid = typeof existing.uid === 'string' ? existing.uid.trim() : '';
  if (existingUid === '') {
    await setDoc(ref, { uid }, { merge: true });
    return;
  }
  if (existingUid !== uid) {
    throw new Error('invite_token_conflict');
  }
}

export async function getUidForInviteToken(inviteToken: string): Promise<string | null> {
  if (!inviteToken) return null;
  const snap = await getDoc(doc(db, INVITE_LOOKUP, inviteToken));
  if (!snap.exists()) return null;
  const u = (snap.data() as { uid?: string }).uid;
  return typeof u === 'string' ? u : null;
}

export async function publishUserChatPublicKey(uid: string, publicJwk: JsonWebKey): Promise<void> {
  await setDoc(
    doc(db, USER_CHAT_KEYS, uid),
    {
      chatPublicKeyJwk: publicJwk,
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );
}

export async function getUserChatPublicJwk(uid: string): Promise<JsonWebKey | null> {
  if (!uid) return null;
  const snap = await getDoc(doc(db, USER_CHAT_KEYS, uid));
  if (!snap.exists()) return null;
  const jwk = (snap.data() as { chatPublicKeyJwk?: JsonWebKey }).chatPublicKeyJwk;
  return jwk && typeof jwk === 'object' ? jwk : null;
}
